import * as React from 'react';
import { useState, useCallback } from 'react';
import { Button, IconButton } from '@mui/material';
import DeleteIcon from '@material-ui/icons/Delete';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import { ExpenseAttachment } from '../../../models';
import { useTranslations } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import './fields.scss';

export interface ExpenseAttachmentsFieldProps {
    id?: string;
    label?: string;
    disabled?: boolean;
    existingAttachments?: ExpenseAttachment[];
    newFiles?: File[];
    onExistingAttachmentsChange?: (attachments: ExpenseAttachment[]) => void;
    onNewFilesChange?: (files: File[]) => void;
}

export const ExpenseAttachmentsField: React.FC<ExpenseAttachmentsFieldProps> = ({
    id,
    label,
    disabled,
    existingAttachments = [],
    newFiles = [],
    onExistingAttachmentsChange,
    onNewFilesChange,
}) => {
    const t = useTranslations();
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;

        const newFileArray = Array.from(files);
        onNewFilesChange?.([...newFiles, ...newFileArray]);

        // Reset the input so the same file can be selected again if removed and re-added
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [newFiles, onNewFilesChange]);

    const handleRemoveExisting = useCallback((fileId: string) => {
        const updated = existingAttachments.filter(a => a.fileId !== fileId);
        onExistingAttachmentsChange?.(updated);
    }, [existingAttachments, onExistingAttachmentsChange]);

    const handleRemoveNew = useCallback((index: number) => {
        const updated = newFiles.filter((_, i) => i !== index);
        onNewFilesChange?.(updated);
    }, [newFiles, onNewFilesChange]);

    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <div className="expense-attachments-field" style={{ marginTop: 'var(--spacer-S)' }}>
            {label && <label style={{ fontSize: '0.875rem', marginBottom: '8px', display: 'block', fontWeight: 500 }}>{label}</label>}

            {/* Existing attachments */}
            {existingAttachments.length > 0 && (
                <div style={{ marginBottom: 'var(--spacer-S)' }}>
                    {existingAttachments.map(attachment => (
                        <div
                            key={attachment.fileId}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacer-XS)',
                                padding: 'var(--spacer-XS)',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                marginBottom: 'var(--spacer-XS)',
                            }}
                        >
                            <AttachFileIcon style={{ fontSize: '1rem', color: '#666' }} />
                            {attachment.url ? (
                                <a
                                    href={attachment.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        flex: 1,
                                        fontSize: '0.875rem',
                                        color: '#1976d2',
                                        textDecoration: 'none',
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                >
                                    {attachment.fileName}
                                </a>
                            ) : (
                                <span style={{ flex: 1, fontSize: '0.875rem' }}>{attachment.fileName}</span>
                            )}
                            {!disabled && (
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemoveExisting(attachment.fileId)}
                                    title={t(TK.remove)}
                                >
                                    <DeleteIcon style={{ fontSize: '1rem' }} />
                                </IconButton>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* New files to be uploaded */}
            {newFiles.length > 0 && (
                <div style={{ marginBottom: 'var(--spacer-S)' }}>
                    {newFiles.map((file, index) => (
                        <div
                            key={`new-${index}`}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacer-XS)',
                                padding: 'var(--spacer-XS)',
                                border: '1px solid #4caf50',
                                borderRadius: '4px',
                                marginBottom: 'var(--spacer-XS)',
                                backgroundColor: '#f1f8f4',
                            }}
                        >
                            <AttachFileIcon style={{ fontSize: '1rem', color: '#4caf50' }} />
                            <span style={{ flex: 1, fontSize: '0.875rem' }}>
                                {file.name} ({formatFileSize(file.size)})
                            </span>
                            {!disabled && (
                                <IconButton
                                    size="small"
                                    onClick={() => handleRemoveNew(index)}
                                    title={t(TK.remove)}
                                >
                                    <DeleteIcon style={{ fontSize: '1rem' }} />
                                </IconButton>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Add file button */}
            {!disabled && (
                <>
                    <input
                        ref={fileInputRef}
                        id={id ? `${id}-file-input` : undefined}
                        type="file"
                        multiple
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />
                    <Button
                        id={id ? `${id}-add-button` : undefined}
                        variant="outlined"
                        size="small"
                        startIcon={<AttachFileIcon />}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        {t(TK.addFile)}
                    </Button>
                </>
            )}

            {existingAttachments.length === 0 && newFiles.length === 0 && disabled && (
                <div style={{ fontSize: '0.875rem', color: '#666', fontStyle: 'italic' }}>
                    {t(TK.noAttachments)}
                </div>
            )}
        </div>
    );
};
